import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../api';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const SIGNER_COLORS = ['#059669','#2563eb','#d97706','#dc2626','#7c3aed','#0891b2'];
const DOC_FIELD_COLOR = '#6b7280';
const DOC_FIELD_TYPES = ['DOC_DATE','DOC_DATE_DAY','DOC_DATE_MONTH','DOC_DATE_YEAR_2','DOC_DATE_YEAR_4','DOC_CONTRACT_NUMBER','DOC_ENTITY_NAME'];
const DOC_FIELD_LABELS = {'DOC_DATE':'📅 Date','DOC_DATE_DAY':'Day','DOC_DATE_MONTH':'Month','DOC_DATE_YEAR_2':'Year 2','DOC_DATE_YEAR_4':'Year 4','DOC_CONTRACT_NUMBER':'# Contract #','DOC_ENTITY_NAME':'🏢 Entity'};
const DOC_FIELD_WIDTHS = {'DOC_DATE':120,'DOC_DATE_DAY':40,'DOC_DATE_MONTH':80,'DOC_DATE_YEAR_2':40,'DOC_DATE_YEAR_4':60,'DOC_CONTRACT_NUMBER':140,'DOC_ENTITY_NAME':180};
const DOC_FIELD_PLACEHOLDERS = {'DOC_DATE':'01/01/2025','DOC_DATE_DAY':'DD','DOC_DATE_MONTH':'Month','DOC_DATE_YEAR_2':'YY','DOC_DATE_YEAR_4':'YYYY','DOC_CONTRACT_NUMBER':'Contract #','DOC_ENTITY_NAME':'Entity Name'};
const isDocField = (type) => DOC_FIELD_TYPES.includes(type);

export default function TemplateDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [showUseModal, setShowUseModal] = useState(false);
  const [useTitle, setUseTitle] = useState('');
  const [signerMappings, setSignerMappings] = useState([]);
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pages, setPages] = useState([]);
  const pageRefs = useRef({});
  const pdfScales = useRef({});
  const [scalesReady, setScalesReady] = useState(0);
  const [editingSigner, setEditingSigner] = useState(null); // {id, roleName} or {new: true, roleName}
  const [placingField, setPlacingField] = useState(null); // {signerId, fieldType, label?}
  const [draggingField, setDraggingField] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragOffset, setDragOffset] = useState({x: 0, y: 0});
  const [resizingField, setResizingField] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [docFieldsOpen, setDocFieldsOpen] = useState(true);
  const [signersOpen, setSignersOpen] = useState(true);
  const [expandedSigners, setExpandedSigners] = useState({});
  const fieldClickedRef = useRef(false);
  const fieldOverlayRefs = useRef({});
  const nextSignerNum = (template?.signers?.length || 0) + 1;

  function signerColor(signerId) {
    if (!signerId) return DOC_FIELD_COLOR;
    const idx = template?.signers?.findIndex(s => s.id === signerId) ?? 0;
    return SIGNER_COLORS[idx % SIGNER_COLORS.length];
  }

  const load = useCallback(() => api(`/esign/templates/${id}`).then(r => r.ok ? r.json() : null).then(setTemplate).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!template) return;
    api(`/esign/templates/${id}/view-url`).then(r => r.ok ? r.json() : null).then(data => {
      if (data?.url) setPdfUrl(data.url);
    });
  }, [id, template]);

  useEffect(() => {
    if (!pdfUrl) return;
    let cancelled = false;
    pdfjsLib.getDocument(pdfUrl).promise.then(pdf => {
      if (cancelled) return;
      const pagePromises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        pagePromises.push(pdf.getPage(i).then(page => {
          const viewport = page.getViewport({scale: 1});
          return {pageNum: i, width: viewport.width, height: viewport.height, page};
        }));
      }
      return Promise.all(pagePromises);
    }).then(pgs => {
      if (!cancelled && pgs) setPages(pgs);
    });
    return () => { cancelled = true; };
  }, [pdfUrl]);

  useEffect(() => {
    let rendered = 0;
    pages.forEach(({pageNum, page, width}) => {
      const canvas = pageRefs.current[pageNum];
      if (!canvas || canvas.dataset.rendered === 'true') return;
      const containerWidth = canvas.parentElement.clientWidth;
      const scale = containerWidth / width;
      pdfScales.current[pageNum] = {scale, pdfWidth: width};
      const viewport = page.getViewport({scale});
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.dataset.rendered = 'true';
      page.render({canvasContext: canvas.getContext('2d'), viewport});
      rendered++;
    });
    if (rendered > 0) setScalesReady(prev => prev + 1);
  }, [pages]);

  // Cancel drag on global mouseup
  useEffect(() => {
    if (!draggingField) return;
    const h = () => { setDraggingField(null); setDragPos(null); };
    window.addEventListener('mouseup', h);
    return () => window.removeEventListener('mouseup', h);
  }, [draggingField]);

  const handlePageClick = async (e, pageNum) => {
    if (fieldClickedRef.current) { fieldClickedRef.current = false; return; }
    if (!placingField) { setSelectedField(null); return; }
    const canvas = pageRefs.current[pageNum];
    const scaleInfo = pdfScales.current[pageNum];
    if (!canvas || !scaleInfo) return;
    const rect = canvas.getBoundingClientRect();
    const pdfX = (e.clientX - rect.left) / scaleInfo.scale;
    const pdfY = (e.clientY - rect.top) / scaleInfo.scale;
    const fw = {'DATE':120,'NAME':160,'EMAIL':180,'STREET_ADDRESS_1':220,'STREET_ADDRESS_2':220,'CITY':120,'STATE':80,'ZIP_CODE':80,'COUNTRY':140,'COUNTRY_ABBR':50,'FIRST_NAME':120,'LAST_NAME':120,'DATE_DAY':40,'DATE_MONTH':80,'DATE_YEAR_4':60,'DATE_YEAR_2':40,'FREE_TEXT':160,...DOC_FIELD_WIDTHS}[placingField.fieldType] || 200;
    const fh = (['SIGNATURE','INITIALS'].includes(placingField.fieldType) ? 60 : 30);
    await api(`/esign/templates/${id}/fields`, {
      method: 'POST',
      body: JSON.stringify({
        signerId: placingField.signerId, fieldType: placingField.fieldType, pageNumber: pageNum,
        xPosition: Math.max(0, pdfX - fw / 2), yPosition: Math.max(0, pdfY - fh / 2),
        width: fw, height: fh, label: placingField.label || undefined,
      }),
    });
    setPlacingField(null);
    load();
  };

  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    fieldClickedRef.current = true;
    setSelectedField(field);
    const rect = e.currentTarget.getBoundingClientRect();
    const ox = e.clientX - rect.left, oy = e.clientY - rect.top;
    const sx = e.clientX, sy = e.clientY;
    let started = false;
    const onMove = (ev) => {
      if (!started && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) > 5) {
        started = true;
        setDragOffset({x: ox, y: oy});
        setDraggingField(field);
      }
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleResizeMouseDown = (e, field, pageNum) => {
    e.stopPropagation(); e.preventDefault();
    const scaleInfo = pdfScales.current[pageNum];
    if (!scaleInfo) return;
    const sx = e.clientX, sy = e.clientY, sw = field.width, sh = field.height;
    const onMove = (ev) => setResizingField({...field, width: Math.max(40, sw + (ev.clientX - sx) / scaleInfo.scale), height: Math.max(20, sh + (ev.clientY - sy) / scaleInfo.scale)});
    const onUp = async (ev) => {
      document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp);
      const nw = Math.max(40, sw + (ev.clientX - sx) / scaleInfo.scale), nh = Math.max(20, sh + (ev.clientY - sy) / scaleInfo.scale);
      setResizingField(null);
      await api(`/esign/templates/${id}/fields/${field.id}`, {
        method: 'PUT', body: JSON.stringify({signerId: field.signerId, fieldType: field.fieldType, pageNumber: pageNum, xPosition: field.xPosition, yPosition: field.yPosition, width: nw, height: nh, label: field.label, fontSize: field.fontSize, fontFamily: field.fontFamily, textAlign: field.textAlign}),
      });
      load();
    };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  };

  const handlePageMouseUp = async (e, pageNum) => {
    if (!draggingField) return;
    e.stopPropagation();
    const canvas = pageRefs.current[pageNum];
    const scaleInfo = pdfScales.current[pageNum];
    if (!canvas || !scaleInfo) return;
    const rect = canvas.getBoundingClientRect();
    const pdfX = Math.max(0, (e.clientX - rect.left - dragOffset.x) / scaleInfo.scale);
    const pdfY = Math.max(0, (e.clientY - rect.top - dragOffset.y) / scaleInfo.scale);
    await api(`/esign/templates/${id}/fields/${draggingField.id}`, {
      method: 'PUT', body: JSON.stringify({signerId: draggingField.signerId, fieldType: draggingField.fieldType, pageNumber: pageNum, xPosition: pdfX, yPosition: pdfY, width: draggingField.width, height: draggingField.height, fontSize: draggingField.fontSize, fontFamily: draggingField.fontFamily, textAlign: draggingField.textAlign, label: draggingField.label}),
    });
    setDraggingField(null); setDragPos(null);
    load();
  };

  const handleDeleteField = async (fieldId) => {
    await api(`/esign/templates/${id}/fields/${fieldId}`, {method: 'DELETE'});
    setSelectedField(null);
    load();
  };

  const handleUpdateFieldStyle = async (field, updates) => {
    await api(`/esign/templates/${id}/fields/${field.id}`, {
      method: 'PUT', body: JSON.stringify({
        signerId: field.signerId, fieldType: field.fieldType, pageNumber: field.pageNumber,
        xPosition: field.xPosition, yPosition: field.yPosition, width: field.width, height: field.height,
        fontSize: updates.fontSize ?? field.fontSize, fontFamily: updates.fontFamily ?? field.fontFamily,
        textAlign: updates.textAlign ?? field.textAlign, label: updates.label !== undefined ? updates.label : field.label,
      }),
    });
    load();
  };

  if (!template) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/templates" className="text-emerald-600 hover:text-emerald-800 text-sm">← Back to Templates</Link>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            {editingTitle !== null ? (
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!editingTitle.trim() || editingTitle.trim() === template.title) { setEditingTitle(null); return; }
                await api(`/esign/templates/${id}`, {method: 'PUT', body: JSON.stringify({title: editingTitle.trim()})});
                setEditingTitle(null); load();
              }} className="flex items-center gap-2">
                <input type="text" value={editingTitle} onChange={e => setEditingTitle(e.target.value)} autoFocus
                  onBlur={() => setEditingTitle(null)}
                  className="text-2xl font-bold text-gray-900 border-b-2 border-emerald-500 outline-none bg-transparent" />
              </form>
            ) : (
              <h2 className="text-2xl font-bold text-gray-900 cursor-pointer hover:text-emerald-700" onClick={() => setEditingTitle(template.title)}>
                {template.title}<span className="text-gray-300 text-sm ml-2">✎</span>
              </h2>
            )}
            <p className="text-gray-500 text-sm mt-1">{template.fileName} • {pages.length || template.pageCount || '?'} pages</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => {
              setUseTitle(template.title);
              setSignerMappings((template.signers || []).map(s => ({templateSignerId: s.id, roleName: s.roleName, name: '', email: ''})));
              setShowUseModal(true);
            }} disabled={!template.signers?.length || !template.fields?.length}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm">
              Use Template
            </button>
            <button onClick={async () => { await api(`/esign/templates/${id}`, {method: 'DELETE'}); navigate('/templates'); }}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm">Delete</button>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-72 flex-shrink-0 space-y-4 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          {/* Document Fields */}
          <div className="bg-white rounded-lg shadow">
            <button onClick={() => setDocFieldsOpen(!docFieldsOpen)} className="w-full p-4 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 text-sm">Document Fields</h3>
              <span className={`text-gray-400 text-xs transition-transform ${docFieldsOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {docFieldsOpen && (
              <div>
                <div className="px-4 pb-3">
                  <p className="text-xs text-gray-500 mb-2">Fields filled by the sender, not signers.</p>
                  <div className="flex gap-1 flex-wrap">
                    {DOC_FIELD_TYPES.map(type => {
                      const active = placingField?.fieldType === type;
                      return (
                        <button key={type} onClick={() => setPlacingField(active ? null : {signerId: null, fieldType: type})}
                          className={`px-2 py-1 rounded text-xs border ${active ? 'bg-gray-600 text-white border-gray-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                          {DOC_FIELD_LABELS[type]}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {template.fields?.filter(f => isDocField(f.fieldType)).length > 0 && (
                  <div className="border-t divide-y">
                    {template.fields.filter(f => isDocField(f.fieldType)).map(f => {
                      const isSelected = selectedField?.id === f.id;
                      const showSettings = isSelected && !['SIGNATURE','INITIALS'].includes(f.fieldType);
                      return (
                        <div key={f.id}>
                          <div className={`px-4 py-2 cursor-pointer ${isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'}`} onClick={() => {
                            const next = isSelected ? null : f;
                            setSelectedField(next);
                            if (next && fieldOverlayRefs.current[f.id]) fieldOverlayRefs.current[f.id].scrollIntoView({behavior: 'smooth', block: 'center'});
                          }}>
                            <div className="flex items-center justify-between">
                              <div className="text-xs">
                                <span className="font-medium">{f.fieldType}</span>
                                <span className="text-gray-400 ml-1">p{f.pageNumber}</span>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteField(f.id); }} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                            </div>
                          </div>
                          {showSettings && (
                            <div className="px-4 pb-2 bg-emerald-50 flex items-center gap-2 flex-wrap">
                              <select value={selectedField.fontFamily || 'Helvetica'}
                                onChange={e => { handleUpdateFieldStyle(selectedField, {fontFamily: e.target.value}); setSelectedField({...selectedField, fontFamily: e.target.value}); }}
                                className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                <option value="Helvetica">Helvetica</option><option value="Times New Roman">Times New Roman</option><option value="Courier">Courier</option>
                              </select>
                              <select value={selectedField.fontSize || 14}
                                onChange={e => { const v = parseInt(e.target.value); handleUpdateFieldStyle(selectedField, {fontSize: v}); setSelectedField({...selectedField, fontSize: v}); }}
                                className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                {[8,9,10,11,12,14,16,18,20,24,28,32,36].map(s => <option key={s} value={s}>{s}pt</option>)}
                              </select>
                              <div className="flex border border-gray-300 rounded overflow-hidden">
                                {['LEFT','CENTER','RIGHT'].map(a => (
                                  <button key={a} onClick={() => { handleUpdateFieldStyle(selectedField, {textAlign: a}); setSelectedField({...selectedField, textAlign: a}); }}
                                    className={`px-1.5 py-1 text-xs ${(selectedField.textAlign || 'LEFT') === a ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                    {a === 'LEFT' ? '⫷' : a === 'CENTER' ? '☰' : '⫸'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Signers */}
          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between">
              <button onClick={() => setSignersOpen(!signersOpen)} className="flex-1 p-4 flex justify-between items-center">
                <h3 className="font-semibold text-gray-900 text-sm">Signers</h3>
                <span className={`text-gray-400 text-xs transition-transform ${signersOpen ? 'rotate-180' : ''}`}>▾</span>
              </button>
              <button onClick={(e) => { e.stopPropagation(); setSignersOpen(true); setEditingSigner({new: true, roleName: `Signer ${nextSignerNum}`}); }} className="text-emerald-600 hover:text-emerald-800 text-sm pr-4">+ Add</button>
            </div>
            {signersOpen && (
              <div>
                {(!template.signers || template.signers.length === 0) && !editingSigner?.new && (
                  <p className="text-center py-4 text-gray-400 text-xs">No signer roles yet</p>
                )}
                {template.signers?.map((s, i) => {
                  const color = SIGNER_COLORS[i % SIGNER_COLORS.length];
                  const isExpanded = expandedSigners[s.id];
                  const signerFields = template.fields?.filter(f => f.signerId === s.id) || [];
                  return (
                    <div key={s.id} className="border-t">
                      <div className="px-4 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {editingSigner?.id === s.id ? (
                            <form onSubmit={async (e) => {
                              e.preventDefault();
                              if (!editingSigner.roleName.trim()) return;
                              await api(`/esign/templates/${id}/signers/${s.id}`, {
                                method: 'PUT', body: JSON.stringify({roleName: editingSigner.roleName.trim(), signOrder: s.signOrder}),
                              });
                              setEditingSigner(null); load();
                            }} className="flex items-center gap-2 flex-1">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor: color}} />
                              <input autoFocus value={editingSigner.roleName} onChange={e => setEditingSigner({...editingSigner, roleName: e.target.value})}
                                onBlur={() => setEditingSigner(null)}
                                className="text-sm font-medium text-gray-900 border-b border-emerald-500 outline-none bg-transparent flex-1 min-w-0" />
                            </form>
                          ) : (
                            <button onClick={() => setExpandedSigners(prev => ({...prev, [s.id]: !prev[s.id]}))} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                              <span className={`text-gray-400 text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▾</span>
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor: color}} />
                              <span className="text-sm font-medium text-gray-900 truncate">{s.roleName}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">#{s.signOrder}</span>
                              {signerFields.length > 0 && <span className="text-xs text-gray-400 flex-shrink-0">({signerFields.length})</span>}
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <button onClick={() => setEditingSigner({id: s.id, roleName: s.roleName})} className="text-gray-300 hover:text-gray-500 text-xs">✎</button>
                          <button onClick={async () => { await api(`/esign/templates/${id}/signers/${s.id}`, {method: 'DELETE'}); load(); }}
                            className="text-red-400 hover:text-red-600 text-xs">✕</button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-3">
                          <div className="flex gap-1 flex-wrap mb-1">
                            {['SIGNATURE','INITIALS','DATE','NAME','EMAIL'].map(type => {
                              const active = placingField?.signerId === s.id && placingField?.fieldType === type;
                              return (
                                <button key={type} onClick={() => setPlacingField(active ? null : {signerId: s.id, fieldType: type})}
                                  className={`px-2 py-1 rounded text-xs border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                  {{'SIGNATURE':'✍ Sign','INITIALS':'AB Init.','DATE':'📅 Date','NAME':'👤 Name','EMAIL':'✉ Email'}[type]}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            {['FIRST_NAME','LAST_NAME','DATE_DAY','DATE_MONTH','DATE_YEAR_4','DATE_YEAR_2'].map(type => {
                              const active = placingField?.signerId === s.id && placingField?.fieldType === type;
                              return (
                                <button key={type} onClick={() => setPlacingField(active ? null : {signerId: s.id, fieldType: type})}
                                  className={`px-2 py-1 rounded text-xs border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                  {{'FIRST_NAME':'First','LAST_NAME':'Last','DATE_DAY':'Day','DATE_MONTH':'Month','DATE_YEAR_4':'Year 4','DATE_YEAR_2':'Year 2'}[type]}
                                </button>
                              );
                            })}
                            <div className="w-full mt-1" />
                            {['STREET_ADDRESS_1','STREET_ADDRESS_2','CITY','STATE','ZIP_CODE','COUNTRY','COUNTRY_ABBR'].map(type => {
                              const active = placingField?.signerId === s.id && placingField?.fieldType === type;
                              return (
                                <button key={type} onClick={() => setPlacingField(active ? null : {signerId: s.id, fieldType: type})}
                                  className={`px-2 py-1 rounded text-xs border ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                  {{'STREET_ADDRESS_1':'Addr 1','STREET_ADDRESS_2':'Addr 2','CITY':'City','STATE':'State','ZIP_CODE':'Zip','COUNTRY':'Country','COUNTRY_ABBR':'Ctry Abbr'}[type]}
                                </button>
                              );
                            })}
                            <button onClick={() => { const label = prompt('Label for this free-text field:'); if (label) setPlacingField({signerId: s.id, fieldType: 'FREE_TEXT', label}); }}
                              className={`px-2 py-1 rounded text-xs border ${placingField?.signerId === s.id && placingField?.fieldType === 'FREE_TEXT' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                              ✏ Free Text
                            </button>
                          </div>
                          {signerFields.length > 0 && (
                            <div className="mt-2 border-t pt-1 divide-y">
                              {signerFields.map(f => {
                                const isSelected = selectedField?.id === f.id;
                                const showSettings = isSelected && !['SIGNATURE','INITIALS'].includes(f.fieldType);
                                return (
                                  <div key={f.id}>
                                    <div className={`py-1.5 cursor-pointer ${isSelected ? 'bg-emerald-50 -mx-4 px-4' : ''}`} onClick={() => {
                                      const next = isSelected ? null : f;
                                      setSelectedField(next);
                                      if (next && fieldOverlayRefs.current[f.id]) fieldOverlayRefs.current[f.id].scrollIntoView({behavior: 'smooth', block: 'center'});
                                    }}>
                                      <div className="flex items-center justify-between">
                                        <div className="text-xs">
                                          <span className="font-medium">{f.fieldType === 'FREE_TEXT' && f.label ? f.label : f.fieldType}</span>
                                          <span className="text-gray-400 ml-1">p{f.pageNumber}</span>
                                        </div>
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteField(f.id); }} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                                      </div>
                                    </div>
                                    {showSettings && (
                                      <div className="pb-2 bg-emerald-50 -mx-4 px-4 flex items-center gap-2 flex-wrap">
                                        <select value={selectedField.fontFamily || 'Helvetica'}
                                          onChange={e => { handleUpdateFieldStyle(selectedField, {fontFamily: e.target.value}); setSelectedField({...selectedField, fontFamily: e.target.value}); }}
                                          className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                          <option value="Helvetica">Helvetica</option><option value="Times New Roman">Times New Roman</option><option value="Courier">Courier</option>
                                        </select>
                                        <select value={selectedField.fontSize || 14}
                                          onChange={e => { const v = parseInt(e.target.value); handleUpdateFieldStyle(selectedField, {fontSize: v}); setSelectedField({...selectedField, fontSize: v}); }}
                                          className="text-xs border border-gray-300 rounded px-1.5 py-1">
                                          {[8,9,10,11,12,14,16,18,20,24,28,32,36].map(sz => <option key={sz} value={sz}>{sz}pt</option>)}
                                        </select>
                                        <div className="flex border border-gray-300 rounded overflow-hidden">
                                          {['LEFT','CENTER','RIGHT'].map(a => (
                                            <button key={a} onClick={() => { handleUpdateFieldStyle(selectedField, {textAlign: a}); setSelectedField({...selectedField, textAlign: a}); }}
                                              className={`px-1.5 py-1 text-xs ${(selectedField.textAlign || 'LEFT') === a ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                              {a === 'LEFT' ? '⫷' : a === 'CENTER' ? '☰' : '⫸'}
                                            </button>
                                          ))}
                                        </div>
                                        {selectedField.fieldType === 'FREE_TEXT' && (
                                          <input type="text" value={selectedField.label || ''} placeholder="Field label"
                                            onChange={e => setSelectedField({...selectedField, label: e.target.value})}
                                            onBlur={e => handleUpdateFieldStyle(selectedField, {label: e.target.value})}
                                            className="text-xs border border-gray-300 rounded px-1.5 py-1 w-full mt-1" />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {editingSigner?.new && (
                  <div className="border-t px-4 py-3">
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!editingSigner.roleName.trim()) return;
                      await api(`/esign/templates/${id}/signers`, {
                        method: 'POST', body: JSON.stringify({roleName: editingSigner.roleName.trim(), signOrder: nextSignerNum}),
                      });
                      setEditingSigner(null); load();
                    }} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor: SIGNER_COLORS[(template.signers?.length || 0) % SIGNER_COLORS.length]}} />
                      <input autoFocus value={editingSigner.roleName} onChange={e => setEditingSigner({...editingSigner, roleName: e.target.value})}
                        onBlur={() => setEditingSigner(null)}
                        className="text-sm font-medium text-gray-900 border-b border-emerald-500 outline-none bg-transparent flex-1 min-w-0" />
                    </form>
                  </div>
                )}
                {placingField && placingField.signerId && (
                  <div className="px-4 py-3 border-t">
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-700">
                      Click on the document to place the field. <button onClick={() => setPlacingField(null)} className="underline ml-1">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* PDF Pages */}
        <div className="flex-1 space-y-4" data-scales={scalesReady}>
          {pages.length === 0 && <div className="text-center py-12 text-gray-400">Loading PDF...</div>}
          {pages.map(({pageNum}) => {
            const scaleInfo = pdfScales.current[pageNum];
            const pageFields = template.fields?.filter(f => f.pageNumber === pageNum) || [];
            return (
              <div key={pageNum} className="relative bg-white rounded-lg shadow">
                <div className="text-xs text-gray-400 px-3 py-1 border-b">Page {pageNum}</div>
                <div className="relative"
                  onClick={(e) => handlePageClick(e, pageNum)}
                  onMouseMove={(e) => { if (draggingField) { const rect = e.currentTarget.getBoundingClientRect(); setDragPos({x: e.clientX - rect.left, y: e.clientY - rect.top, pageNum}); } }}
                  onMouseUp={(e) => { setDragPos(null); handlePageMouseUp(e, pageNum); }}
                  style={{cursor: placingField ? 'crosshair' : draggingField ? 'grabbing' : 'default'}}>
                  <canvas ref={el => { pageRefs.current[pageNum] = el; }} className="w-full block" />
                  {scaleInfo && pageFields.map(f => {
                    const rf = resizingField?.id === f.id ? resizingField : f;
                    const left = f.xPosition * scaleInfo.scale;
                    const top = f.yPosition * scaleInfo.scale;
                    const w = rf.width * scaleInfo.scale;
                    const h = rf.height * scaleInfo.scale;
                    const color = signerColor(f.signerId);
                    const signer = template.signers?.find(s => s.id === f.signerId);
                    const isTextField = !['SIGNATURE','INITIALS'].includes(f.fieldType);
                    const placeholderText = isTextField ? (isDocField(f.fieldType) ? (DOC_FIELD_PLACEHOLDERS[f.fieldType] || f.fieldType) : ({'DATE':'01/01/2025','NAME':'Full Name','EMAIL':'email@example.com','STREET_ADDRESS_1':'123 Main St','STREET_ADDRESS_2':'Apt 4B','CITY':'City','STATE':'ST','ZIP_CODE':'12345','COUNTRY':'United States','COUNTRY_ABBR':'US','FIRST_NAME':'First','LAST_NAME':'Last','DATE_DAY':'DD','DATE_MONTH':'Month','DATE_YEAR_4':'YYYY','DATE_YEAR_2':'YY','FREE_TEXT': f.label || 'Free text'}[f.fieldType] || f.fieldType)) : null;
                    const fontSize = isTextField ? (f.fontSize || 14) * scaleInfo.scale : undefined;
                    const fontFamily = isTextField ? (f.fontFamily || 'Helvetica') : undefined;
                    const textAlign = isTextField ? (f.textAlign || 'LEFT').toLowerCase() : undefined;
                    const isSelected = selectedField?.id === f.id;
                    return (
                      <div key={f.id}
                        ref={el => { fieldOverlayRefs.current[f.id] = el; }}
                        onMouseDown={(e) => handleFieldMouseDown(e, f)}
                        style={{
                          position: 'absolute', left, top, width: w, height: h,
                          border: isSelected ? '3px solid #000' : `2px solid ${color}`,
                          backgroundColor: isSelected ? `${color}40` : `${color}20`,
                          cursor: 'grab',
                          opacity: draggingField?.id === f.id ? 0.3 : 1,
                          zIndex: isSelected ? 10 : 1,
                          boxShadow: isSelected ? '0 0 0 2px rgba(0,0,0,0.3)' : 'none',
                        }}
                        className="rounded select-none group">
                        {isTextField ? (
                          <span className="absolute bottom-0 left-0 right-0 truncate px-1" style={{color, fontSize, fontFamily, textAlign, lineHeight: 1.2}}>
                            {placeholderText}
                          </span>
                        ) : (
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium truncate px-1" style={{color}}>
                            {f.fieldType === 'SIGNATURE' ? '✍' : 'AB'}
                            {signer ? ` ${signer.roleName}` : ''}
                          </span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteField(f.id); }}
                          className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full text-xs leading-none hidden group-hover:flex items-center justify-center">×</button>
                        <div onMouseDown={(e) => handleResizeMouseDown(e, f, pageNum)}
                          className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize hidden group-hover:block"
                          style={{backgroundColor: color, borderRadius: '0 0 4px 0'}} />
                      </div>
                    );
                  })}
                  {draggingField && dragPos && dragPos.pageNum === pageNum && (
                    <div style={{
                      position: 'absolute', left: dragPos.x - dragOffset.x, top: dragPos.y - dragOffset.y,
                      width: draggingField.width * (pdfScales.current[pageNum]?.scale || 1),
                      height: draggingField.height * (pdfScales.current[pageNum]?.scale || 1),
                      border: '2px dashed #059669', backgroundColor: 'rgba(5,150,105,0.1)', borderRadius: 4, pointerEvents: 'none',
                    }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Use Template Modal */}
      {showUseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUseModal(false)}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (signerMappings.some(m => !m.name.trim() || !m.email.trim())) return;
            setSending(true);
            const res = await api(`/esign/templates/${id}/use`, {
              method: 'POST',
              body: JSON.stringify({
                title: useTitle.trim() || template.title,
                signerMappings: signerMappings.map(m => ({templateSignerId: m.templateSignerId, name: m.name.trim(), email: m.email.trim()})),
              }),
            });
            setSending(false);
            if (res.ok) {
              const doc = await res.json();
              navigate(`/documents/${doc.id}`);
            }
          }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Use Template</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
                <input type="text" value={useTitle} onChange={e => setUseTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
              </div>
              {signerMappings.map((m, i) => (
                <div key={m.templateSignerId} className="border border-gray-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full inline-block" style={{backgroundColor: SIGNER_COLORS[i % SIGNER_COLORS.length]}} />
                    {m.roleName}
                  </p>
                  <div className="space-y-2">
                    <input type="text" value={m.name} placeholder="Full name" onChange={e => {
                      const updated = [...signerMappings]; updated[i] = {...m, name: e.target.value}; setSignerMappings(updated);
                    }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm" />
                    <input type="email" value={m.email} placeholder="Email address" onChange={e => {
                      const updated = [...signerMappings]; updated[i] = {...m, email: e.target.value}; setSignerMappings(updated);
                    }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm" />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setShowUseModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={sending || signerMappings.some(m => !m.name.trim() || !m.email.trim())}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                {sending ? 'Creating...' : 'Create Document'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
